import React, { useEffect, useState } from "react";
import { FooterRow } from "../../../../types";
import { Button } from "../../../UI/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "../../../common/Grid";
import { Input, Label } from "../../../UI/Forms/FormElements";
import { FormControl } from "@mui/material";
import { observer } from "mobx-react";
import { Editor } from "react-draft-wysiwyg";
import "../../../../../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { ContentState, convertToRaw, EditorState } from "draft-js";
import Feedback from "../../../UI/Forms/FormElements/Feedback";

type Props = {
  footerRow?: FooterRow;
  isSubmitting: boolean;
  onSubmit: (values: FooterRow) => void;
  onCancel: () => void;
};

const FooterForm: React.FC<Props> = observer(
  ({ footerRow, isSubmitting, onSubmit, onCancel }) => {
    const [editorState, setEditorState] = useState<EditorState | undefined>(
      undefined
    );
    const { t } = useTranslation();

    const formik = useFormik({
      initialValues: {
        id: footerRow?.id || undefined,
        html: footerRow?.html || "",
        orderNumber: footerRow?.orderNumber || 0,
      },
      enableReinitialize: true,
      onSubmit: onSubmit,
      validationSchema: Yup.object().shape(
        {
          html: Yup.string().required(
            t("components.common.footerForm.htmlRequired")
          ),
          orderNumber: Yup.number().min(
            1,
            t("components.common.footerForm.orderNumberRequired")
          ),
        },
        [["onClickUrl", "category"]]
      ),
    });

    useEffect(() => {
      if (footerRow) {
        const contentBlock = htmlToDraft(footerRow.html);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(
            contentBlock.contentBlocks
          );
          const editorState = EditorState.createWithContent(contentState);
          setEditorState(editorState);
        }
      }
    }, [footerRow]);

    return (
      <form onSubmit={formik.handleSubmit}>
        <Container pb={"20px"}>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="orderNumber">
                  {t("components.common.footerForm.orderNumber")}
                </Label>
                <Input
                  type="number"
                  id="orderNumber"
                  name="orderNumber"
                  placeholder={t(
                    "components.common.footerForm.orderNumberPlaceholder"
                  )}
                  value={formik.values.orderNumber}
                  onChange={formik.handleChange}
                  state={formik.errors.orderNumber ? "error" : "success"}
                  showState={true}
                  feedbackText={formik.getFieldMeta("orderNumber").error}
                />
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="html">
                  {t("components.common.footerForm.html")}
                </Label>
                <Editor
                  editorState={editorState}
                  editorStyle={{
                    border: "1px solid #F1F1F1",
                    padding: "0 20px",
                  }}
                  onEditorStateChange={(editorState) => {
                    const html = draftToHtml(
                      convertToRaw(editorState.getCurrentContent())
                    );
                    formik.setFieldValue("html", html);
                    setEditorState(editorState);
                  }}
                />
                {formik.errors.html && (
                  <Feedback
                    state={"error"}
                    showState={true}
                    showErrorOnly={true}
                  >
                    {formik.errors.html}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
        </Container>

        <Row>
          <Col textAlign="end">
            <Button
              type={"reset"}
              onClick={onCancel}
              marginRight="10px"
              color="light"
              disabled={isSubmitting}
            >
              {t("common.cancel")}
            </Button>
            <Button type={"submit"}>{t("common.save")}</Button>
          </Col>
        </Row>
      </form>
    );
  }
);

export default FooterForm;
